import { Container, Button, Table, Modal, Card, Row, Col, OverlayTrigger, Tooltip, Spinner, Form, InputGroup } from "react-bootstrap";
import { useState, useEffect, useCallback } from 'react';
import { protectedResources, scopes } from "../authConfig";
import { useLocation, useNavigate } from 'react-router-dom';
import useFetchWithMsal from '../hooks/useFetchWithMsal';
import masterDataConfig from '../settings/masterData.json';
import MasterDataEditForm from '../components/MasterDataEditForm';
import { FaPlus, FaEdit, FaTrash, FaHome, FaSearch, FaTimes } from 'react-icons/fa';

export const MasterDataManagement = (props) => {

    // クエリパラメーターのtypeを取得
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const type = query.get('type');

    const [masterData, setMasterData] = useState([]);
    const [columns, setColumns] = useState([]);
    const [isUpdate, setIsUpdate] = useState(false);
    const [masterType, setMasterType] = useState('');
    const [show, setShow] = useState(false);
    const [selectedData, setSelectedData] = useState(null);
    const { execute } = useFetchWithMsal({
        scopes: scopes.callapi
    });

    // 外部キーのマスタデータを保持するステート
    const [foreignKeyData, setForeignKeyData] = useState({});
    const [loading, setLoading] = useState(true);
    
    // 主キーによるフィルタリング用のステート
    const [filterValue, setFilterValue] = useState("");
    const [filteredData, setFilteredData] = useState([]);
    const [primaryKeyColumn, setPrimaryKeyColumn] = useState(null);
    const [displayNameColumn, setDisplayNameColumn] = useState(null);

    // マスタデータと外部キーデータを一緒に取得する
    const fetchMasterData = useCallback(async () => {
        try {
            setLoading(true);
            // まず、現在のマスタタイプのデータを取得
            const response = await execute('GET', protectedResources.getMasterdatas.endpoint + "/" + type);
            console.log("Response:", response);

            let filteredResponse = [];
            if (response && Array.isArray(response)) {
                filteredResponse = response.map(item => {
                    const newItem = {};
                    Object.keys(item).forEach(key => {
                        if (!key.includes('_')) {
                            newItem[key] = item[key];
                        }
                    });
                    return newItem;
                });
                setMasterData(filteredResponse);
                console.log("Filtered Response:", filteredResponse);
            } else {
                console.log("Response is undefined");
                setMasterData([]);
                setIsUpdate(false); // エラー時に再実行を防ぐ
            }

            // 外部キーの情報を取得
            if (type) {
                const master = masterDataConfig.masters.find(m => m.type === type);
                if (master) {
                    const foreignKeyColumns = master.columns.filter(column => column.isForeignKey);
                    
                    // 各外部キー列についてマスタデータを取得
                    const foreignData = {};
                    await Promise.all(foreignKeyColumns.map(async (column) => {
                        // foreignTableが存在する場合のみリクエスト
                        if (column.foreignTable) {
                            try {
                                const foreignResponse = await execute('GET', protectedResources.getMasterdatas.endpoint + "/" + column.foreignTable);
                                if (foreignResponse && Array.isArray(foreignResponse)) {
                                    // 外部キーテーブルの名前列を特定 (XXXNameの形式)
                                    const nameField = column.foreignTable + "Name";
                                    
                                    // IDと名前のマッピングを作成
                                    const mapping = {};
                                    foreignResponse.forEach(item => {
                                        mapping[item.id] = item[nameField] || item.displayName || item.name || `ID: ${item.id}`;
                                    });
                                    
                                    foreignData[column.name] = mapping;
                                }
                            } catch (error) {
                                console.error(`Error fetching foreign key data for ${column.foreignTable}:`, error);
                            }
                        }
                    }));
                    
                    setForeignKeyData(foreignData);
                }
            }
        } catch (error) {
            console.error("Error fetching master data:", error);
            setMasterData([]);
            setIsUpdate(false); // エラー時に再実行を防ぐ
        } finally {
            setLoading(false);
        }
    }, [execute, type]);

    const navigate = useNavigate();
    const handleButtonClick = (managementType) => {
        navigate(`/`);
    };

    useEffect(() => {
        fetchMasterData();
        setIsUpdate(false);
        setColumns(masterDataConfig.columns);
        if (type) {
            const master = masterDataConfig.masters.find(m => m.type === type);
            if (master) {
                setColumns(master.columns || []);
                setMasterType(master.name);
                // 主キーのカラムを探す
                const pkCol = master.columns.find(col => col.isPrimaryKey);
                setPrimaryKeyColumn(pkCol);
                
                // 表示名となるカラムを特定する
                // 命名規則：typeNameパターンを優先（例：companyName, seasonName）
                // そうでなければ最初の非主キー、非外部キー、非隠し属性のテキスト列
                const nameField = type + "Name";
                let displayCol = master.columns.find(col => col.name === nameField);
                
                if (!displayCol) {
                    // typeNameがない場合は、表示に適した最初のテキスト列を探す
                    displayCol = master.columns.find(col => 
                        !col.isPrimaryKey && 
                        !col.isForeignKey && 
                        !col.hidden && 
                        (col.type === 'text' || col.name.toLowerCase().includes('name'))
                    );
                }
                
                setDisplayNameColumn(displayCol);
            } else {
                setColumns([]);
                setMasterType('');
                setPrimaryKeyColumn(null);
                setDisplayNameColumn(null);
            }
        }
    }, [fetchMasterData, isUpdate, type]);

    // フィルタリングロジック
    useEffect(() => {
        if (masterData && masterData.length > 0) {
            if (filterValue.trim() === '') {
                setFilteredData(masterData);
            } else {
                const filtered = masterData.filter(item => {
                    // 検索キーワード（小文字に変換）
                    const keyword = filterValue.toLowerCase();
                    
                    // 主キーの値がある場合はその値で検索
                    if (primaryKeyColumn) {
                        const pkValue = String(item[primaryKeyColumn.name] || '').toLowerCase();
                        if (pkValue.includes(keyword)) return true;
                    }
                    
                    // 表示名カラムの値がある場合はその値でも検索
                    if (displayNameColumn) {
                        const displayValue = String(item[displayNameColumn.name] || '').toLowerCase();
                        if (displayValue.includes(keyword)) return true;
                    }

                    // 外部キーの各カラムとその表示名で検索
                    const foreignKeyColumns = columns.filter(col => col.isForeignKey && !col.hidden);
                    for (const fkColumn of foreignKeyColumns) {
                        // 外部キーの値で検索
                        const fkValue = String(item[fkColumn.name] || '').toLowerCase();
                        if (fkValue.includes(keyword)) return true;
                        
                        // 外部キーに対応する表示名で検索
                        if (foreignKeyData[fkColumn.name] && item[fkColumn.name]) {
                            const fkDisplayValue = String(foreignKeyData[fkColumn.name][item[fkColumn.name]] || '').toLowerCase();
                            if (fkDisplayValue.includes(keyword)) return true;
                        }
                    }
                    
                    // どれにも一致しない場合
                    return false;
                });
                setFilteredData(filtered);
            }
        } else {
            setFilteredData(masterData);
        }
    }, [masterData, filterValue, primaryKeyColumn, displayNameColumn, columns, foreignKeyData]);

    const handleCreate = () => {
        setSelectedData({});
        setShow(true);
    };

    const handleUpdate = (data) => {
        setSelectedData(data);
        setShow(true);
    };

    const handleDelete = async (id) => {
        await execute("DELETE", protectedResources.deleteMasterData.endpoint + "/" + type + "?id=" + id);
        setIsUpdate(true);
    };

    // typeが存在しない場合
    if (!type) {
        return (
            <Container className="py-5">
                <Card className="border-0 shadow-sm text-center p-5">
                    <Card.Body>
                        <h1 className="mb-4">管理者トップページ</h1>
                        <p className="text-muted mb-4">管理対象を選択してください。</p>
                        <Button variant="primary" size="lg" onClick={() => navigate('/')}>
                            ホームに戻る
                        </Button>
                    </Card.Body>
                </Card>
            </Container>
        );
    }

    return (
        <Container className="py-5">
            <Card className="border-0 shadow-sm mb-5">
                <Card.Body>
                    <Row className="align-items-center mb-2">
                        <Col>
                            <h1 className="fw-bold mb-0">{masterType}管理</h1>
                        </Col>
                        <Col xs="auto">
                            <Button 
                                variant="outline-secondary" 
                                className="d-flex align-items-center gap-2" 
                                onClick={() => handleButtonClick()}
                            >
                                <FaHome /> 管理トップ
                            </Button>
                        </Col>
                    </Row>
                </Card.Body>
            </Card>
            
            <Card className="border-0 shadow-sm">
                <Card.Header className="bg-light py-3">
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">{masterType}一覧</h5>
                        <Button 
                            variant="primary" 
                            className="d-flex align-items-center gap-2" 
                            onClick={() => handleCreate()}
                            disabled={loading}
                        >
                            <FaPlus /> 新規登録
                        </Button>
                    </div>
                    <div className="mt-3">
                        <Form>
                            <InputGroup>
                                <InputGroup.Text>
                                    <FaSearch />
                                </InputGroup.Text>
                                <Form.Control
                                    type="text"
                                    placeholder={
                                        displayNameColumn 
                                            ? `${primaryKeyColumn?.displayName || "ID"}・${displayNameColumn.displayName}・関連項目で検索`
                                            : primaryKeyColumn 
                                                ? `${primaryKeyColumn.displayName}または関連項目で検索` 
                                                : "検索"
                                    }
                                    value={filterValue}
                                    onChange={(e) => setFilterValue(e.target.value)}
                                />
                                {filterValue && (
                                    <Button variant="outline-secondary" onClick={() => setFilterValue("")}>
                                        <FaTimes />
                                    </Button>
                                )}
                            </InputGroup>
                        </Form>
                    </div>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <p className="mt-2">データを読み込み中...</p>
                        </div>
                    ) : Array.isArray(masterData) && masterData.length === 0 ? (
                        <div className="text-center py-5">
                            <p className="text-muted">データがありません。</p>
                        </div>
                    ) : filteredData.length === 0 && filterValue.trim() !== '' ? (
                        <div className="text-center py-5">
                            <p className="text-muted">検索条件に一致するデータがありません。</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="align-middle">
                                <thead className="table-light">
                                    <tr>
                                        {columns.filter(column => !column.hidden).map((column) => (
                                            <th key={column.name}>{column.displayName}</th>
                                        ))}
                                        <th className="text-center">操作</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Array.isArray(filteredData) && filteredData.map((data) => (
                                        <tr key={data.id}>
                                            {columns.filter(column => !column.hidden).map((column) => {
                                                // 各セル用のツールチップコンテンツを作成
                                                let tooltipContent = [];
                                                
                                                // 外部キーの場合は参照IDのみを表示
                                                if (column.isForeignKey && data[column.name]) {
                                                    const displayName = column.displayName.replace(/ID$/, '');
                                                    tooltipContent.push(`${displayName}の参照ID: ${data[column.name]}`);
                                                } 
                                                // 外部キー以外の列は、主キーIDのみ表示
                                                else {
                                                    // 主キーを探す
                                                    const primaryKeyColumn = columns.find(col => col.isPrimaryKey);
                                                    if (primaryKeyColumn && data[primaryKeyColumn.name]) {
                                                        tooltipContent.push(`${primaryKeyColumn.displayName}: ${data[primaryKeyColumn.name]}`);
                                                    }
                                                }
                                                
                                                // 外部キーの場合は名称のみを表示
                                                let cellContent;
                                                if (column.isForeignKey && data[column.name] && foreignKeyData[column.name]) {
                                                    cellContent = foreignKeyData[column.name][data[column.name]] || '';
                                                } else if (column.type === 'dropdown' && data[column.name]) {
                                                    // ドロップダウンの場合は、値に対応する表示名を取得
                                                    const option = column.values?.find(opt => opt.value === data[column.name]);
                                                    cellContent = option ? option.displayName : data[column.name];
                                                } else {
                                                    cellContent = data[column.name] !== null && data[column.name] !== undefined ? data[column.name] : '';
                                                }
                                                
                                                // ツールチップの内容がない場合はツールチップを表示しない
                                                if (tooltipContent.length === 0) {
                                                    return <td key={`${data.id}-${column.name}`}>{String(cellContent)}</td>;
                                                }
                                                
                                                return (
                                                    <OverlayTrigger
                                                        key={`${data.id}-${column.name}`}
                                                        placement="top"
                                                        overlay={
                                                            <Tooltip id={`tooltip-${data.id}-${column.name}`} style={{ maxWidth: '300px' }}>
                                                                <pre style={{
                                                                    margin: 0,
                                                                    textAlign: 'left',
                                                                    whiteSpace: 'pre-wrap',
                                                                    fontSize: '12px',
                                                                    fontFamily: 'monospace'
                                                                }}>
                                                                    {tooltipContent.join('\n')}
                                                                </pre>
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <td>{String(cellContent)}</td>
                                                    </OverlayTrigger>
                                                );
                                            })}
                                            <td>
                                                <div className="d-flex justify-content-center gap-2">
                                                    <Button 
                                                        variant="outline-primary" 
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => handleUpdate(data)}
                                                    >
                                                        <FaEdit />
                                                    </Button>
                                                    <Button 
                                                        variant="outline-danger" 
                                                        size="sm"
                                                        className="d-flex align-items-center"
                                                        onClick={() => handleDelete(data.id)}
                                                    >
                                                        <FaTrash />
                                                    </Button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>
            
            <Modal show={show} onHide={() => { setShow(false); setSelectedData(null); }} size="lg">
                <Modal.Header closeButton className="bg-light">
                    <Modal.Title>{selectedData?.id ? `${masterType}編集` : `${masterType}新規登録`}</Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4">
                    {selectedData && <MasterDataEditForm type={type} data={selectedData} onDone={() => { setShow(false); setSelectedData(null); setIsUpdate(true); }} />}
                </Modal.Body>
            </Modal>
        </Container>
    );
};

export default MasterDataManagement;